/**
 * The Drop / Drop Item / Mint rule set, as the backend actually defines it.
 *
 * This file is pure and DOM-free so the rules are tested directly rather than
 * inferred from a rendered page, and so every surface (drop page, item card,
 * the two item form routes, the listing) reads the SAME rule instead of
 * re-deriving its own slightly different one — which is how the previous
 * implementation ended up with three incompatible ideas of "live".
 *
 * WHAT THE BACKEND STATES, and what this file therefore enforces:
 *
 *  - A Drop's status is one of `draft`, `send_to_review`, `accept`, `active`
 *    (plus terminal/negative states the backend may add, e.g. `reject`).
 *  - The owner may EDIT a Drop only while it is `draft` or `send_to_review`.
 *  - An authenticated owner may VIEW their own Drops in every status.
 *  - The public may view only `accept` and `active`.
 *  - A Drop has a REQUIRED start time and an OPTIONAL end time.
 *  - Mint is allowed only when the status is `accept` or `active`, the start
 *    time has arrived, and the end time (when there is one) has not passed.
 *  - A Drop with no end time stays mintable until the owner expires it
 *    manually (`drop/expire_at/{dropId}`), which is the same field: expiring
 *    sets the end time. After it passes, no further mint is allowed.
 *  - Drop Items may be CREATED by the owner while the PARENT drop is one of
 *    `draft`, `send_to_review`, `accept`, or `active`.
 *  - Existing Drop Items may be edited in the same four statuses; deleting
 *    remains limited to `draft` and `send_to_review`.
 *
 * WHAT IS DELIBERATELY NOT HERE: any notion of a purchase quantity, a charged
 * amount, or a per-unit debit. `drop-item/mint/{dropItemId}` takes the item id
 * and nothing else, so this client has no quantity to sequence and no amount it
 * can honestly claim was charged.
 */

/** The statuses the backend is known to use. Anything else lands in `other`. */
export type DropRawStatus =
  | "draft"
  | "send_to_review"
  | "accept"
  | "active"
  | "reject"
  | "expired"
  | "other";

/** Statuses in which a drop is mintable at all, before any time check. */
export const MINTABLE_STATUSES: readonly DropRawStatus[] = ["accept", "active"];
/** Statuses in which the owner may still change the drop or its items. */
export const EDITABLE_STATUSES: readonly DropRawStatus[] = ["draft", "send_to_review"];
/** A published drop can receive new items, even though its own fields and
 * existing items remain locked. Keep this separate from EDITABLE_STATUSES. */
export const ITEM_CREATABLE_STATUSES: readonly DropRawStatus[] = ["draft", "send_to_review", "accept", "active"];
/** Statuses a visitor with no session may see. */
export const PUBLICLY_VISIBLE_STATUSES: readonly DropRawStatus[] = ["accept", "active"];

/**
 * Maps the raw backend string onto the known set. Unrecognised values become
 * `other`, which every rule below treats as "not permitted" — an unknown status
 * must never fall through to the permissive branch.
 */
export function toDropRawStatus(value: unknown): DropRawStatus {
  if (typeof value !== "string") return "other";
  const v = value.trim().toLowerCase();
  if (v === "draft") return "draft";
  if (v === "send_to_review" || v === "send-to-review") return "send_to_review";
  if (v === "accept" || v === "accepted") return "accept";
  if (v === "active") return "active";
  if (v === "reject" || v === "rejected") return "reject";
  if (v === "expired") return "expired";
  return "other";
}

/**
 * Parses the backend's `YYYY-MM-DD HH:mm:ss` (and plain ISO) into epoch ms.
 *
 * The space-separated form is NOT valid ISO 8601, and `new Date(...)` parses it
 * inconsistently across engines, so it is normalised to `T`-separated first.
 * The backend sends no timezone offset, so a value without one is read as LOCAL
 * time — the same wall-clock the owner typed into the form. Returns null for
 * anything unparseable, and every caller treats null as "unknown", never "now".
 */
export function parseDropDateTime(value: string | null | undefined): number | null {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(trimmed)
    ? trimmed.replace(" ", "T")
    : trimmed;
  const ms = new Date(normalized).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Formats a `Date` as the `YYYY-MM-DD HH:mm:ss` the expire endpoint requires. */
export function toApiDateTime(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    ` ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

export type DropTiming = {
  status: DropRawStatus;
  /** Required by the backend; still nullable here because a payload may omit it. */
  startAt: string | null;
  /** Optional. Manual expiry writes this same field. */
  endAt: string | null;
};

/**
 * Builds the timing triple from a normalised `Drop`.
 *
 * Use this instead of passing a `Drop` straight into a rule: a `Drop` carries
 * BOTH `status` (the derived display phase) and `rawStatus` (the backend's),
 * and they overlap on the word "draft". Reading the wrong one compiles in some
 * shapes and silently decides permissions from a display label.
 */
export function dropTiming(drop: {
  rawStatus: DropRawStatus;
  startAt: string | null;
  endAt: string | null;
}): DropTiming {
  return { status: drop.rawStatus, startAt: drop.startAt, endAt: drop.endAt };
}

export type MintBlockedReason =
  /** The drop's status is not `accept`/`active`. */
  | "status"
  /** The payload states no start time, so "has it started" is not answerable. */
  | "start-time-unknown"
  /** The start time has not arrived yet. */
  | "not-started"
  /** The end time has passed — either the drop's own, or a manual expiry. */
  | "ended";

export type Mintability =
  | { allowed: true; reason: null }
  | { allowed: false; reason: MintBlockedReason };

/**
 * Whether a mint may be attempted right now.
 *
 * `now` is injected rather than read from the clock so this is testable and so
 * a caller can evaluate the same drop at a specific instant.
 *
 * A missing start time BLOCKS. The start time is required by the backend, so
 * its absence means the payload is not stating one — and defaulting an unknown
 * start to "already started" is exactly the kind of invented permission this
 * correction exists to remove.
 */
export function getMintability(drop: DropTiming, now: number = Date.now()): Mintability {
  if (!MINTABLE_STATUSES.includes(drop.status)) {
    return { allowed: false, reason: "status" };
  }
  const startMs = parseDropDateTime(drop.startAt);
  if (startMs === null) return { allowed: false, reason: "start-time-unknown" };
  if (now < startMs) return { allowed: false, reason: "not-started" };

  const endMs = parseDropDateTime(drop.endAt);
  // No end time is NOT an implicit end: the drop stays mintable until the owner
  // expires it manually, which writes this same field.
  if (endMs !== null && now >= endMs) return { allowed: false, reason: "ended" };

  return { allowed: true, reason: null };
}

/** The owner may change the drop itself only while it is draft/send_to_review. */
export function canEditDrop(status: DropRawStatus): boolean {
  return EDITABLE_STATUSES.includes(status);
}

/**
 * Existing Drop Items follow the PARENT drop, not their own status: deletion
 * stays limited to draft/send_to_review.
 */
export function canManageDropItems(status: DropRawStatus): boolean {
  return EDITABLE_STATUSES.includes(status);
}

/** New items remain creatable after acceptance and while a drop is active. */
export function canCreateDropItems(status: DropRawStatus): boolean {
  return ITEM_CREATABLE_STATUSES.includes(status);
}

/** Accepted and active drops may also correct an existing item's metadata. */
export function canEditDropItems(status: DropRawStatus): boolean {
  return ITEM_CREATABLE_STATUSES.includes(status);
}

/**
 * The drop's end time has passed — its own, or one set by a manual expiry.
 * From then on the drop is closed for its owner too: no new item, no change to
 * the drop or to an item, no further expiry. Only the mint of what is left
 * stays open, and only for the owner (`getMintabilityFor`).
 */
export function isDropEnded(endAt: string | null | undefined, now: number = Date.now()): boolean {
  const endMs = parseDropDateTime(endAt ?? null);
  return endMs !== null && now >= endMs;
}

/**
 * The mint gate for one visitor: `getMintability`, except that once the drop
 * has ENDED its owner may still mint the items left unminted — nobody else
 * can. Every other block (status, start) holds for the owner as well.
 */
export function getMintabilityFor(drop: DropTiming, isOwner: boolean, now: number = Date.now()): Mintability {
  const m = getMintability(drop, now);
  if (!m.allowed && m.reason === "ended" && isOwner) return { allowed: true, reason: null };
  return m;
}

/**
 * Manual expiry is an action on a running campaign, so it is offered only for
 * `accept`/`active` — the same statuses the backend accepts it for. It is NOT
 * gated on there already being an end time: replacing one is a legitimate
 * shortening, and a drop with none is the main case this exists for.
 */
export function canExpireDrop(status: DropRawStatus): boolean {
  return MINTABLE_STATUSES.includes(status);
}

/**
 * THE FINAL LOCK. `confirmation_at` is written by the backend — never by the
 * client, never from a form — the moment the drop became a collection on
 * chain. While it is null the status rules above decide; once it holds a
 * value the backend refuses (403) every change, even where the status would
 * still allow one: drop/update, drop/delete, drop/expire_at, and
 * drop-item/create, /update and /delete. So every owner control is gated on
 * this AND its status rule.
 *
 * Mint and buy are NOT gated by it — they stay on start_time / expire_time.
 */
export function isDropFinalized(confirmationAt: string | null | undefined): boolean {
  return typeof confirmationAt === "string" && confirmationAt.trim() !== "";
}

/**
 * Visibility. An authenticated owner sees their own drop in EVERY status; a
 * non-owner (signed in or not) sees only `accept`/`active`.
 */
export function canViewDrop(status: DropRawStatus, isOwner: boolean): boolean {
  return isOwner || PUBLICLY_VISIBLE_STATUSES.includes(status);
}

/**
 * The coarse phase the cards and badges render. Distinct from the raw status
 * because `accept`/`active` cover a scheduled, a running and a finished
 * campaign, and one badge cannot say which without the times.
 */
export type DropPhase = "draft" | "in_review" | "upcoming" | "minting" | "ended" | "unavailable";

export function getDropPhase(drop: DropTiming, now: number = Date.now()): DropPhase {
  if (drop.status === "draft") return "draft";
  if (drop.status === "send_to_review") return "in_review";
  if (!MINTABLE_STATUSES.includes(drop.status)) return "unavailable";

  const mintability = getMintability(drop, now);
  if (mintability.allowed) return "minting";
  switch (mintability.reason) {
    case "ended":
      return "ended";
    case "not-started":
      return "upcoming";
    default:
      // A mintable status whose start time is unstated is not "upcoming" —
      // there is no date to count down to. It is simply not available.
      return "unavailable";
  }
}
